import React, { useEffect } from 'react';
import { SalesOrderDataForm } from '../../../../app/models/SalesOrder';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import { Society } from '../../../../app/models/Society';
import { SocietyClientDataForm } from '../../../../app/models/SocietyClient';
import { Currency } from '../../../../app/models/Currency';
import { PaymentMethod } from '../../../../app/models/PaymentMethod';
import { PaymentCondition } from '../../../../app/models/PaymentCondition';
import { Sector } from '../../../../app/models/Sector';
import { Location } from '../../../../app/models/Location';
import { Incoterm } from '../../../../app/models/Incoterm';

interface SalesOrderFormProps {
    salesOrder: SalesOrderDataForm;
    setSalesOrder: (salesOrder: SalesOrderDataForm) => void;
    societies: Society[];
    societyClients: SocietyClientDataForm[];
    clientContacts: any[];
    sectors: Sector[];
    currencies: Currency[];
    paymentMethods: PaymentMethod[];
    paymentConditions: PaymentCondition[];
    locations: Location[];
    incoterms: Incoterm[];
    errorFields?: any;
}

const SalesOrderForm = ({
    salesOrder,
    setSalesOrder,
    societies,
    societyClients,
    clientContacts,
    sectors,
    currencies,
    paymentMethods,
    paymentConditions,
    locations,
    incoterms,
    errorFields
}: SalesOrderFormProps) => {
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields]);

    const handleChangeSelectTwo = (name: string, value: number | string | null) => {
        setSalesOrder({ ...salesOrder, [name]: value });
    };

    // Cuando se selecciona un cliente, resetear contacto
    const handleChangeClient = (clientId: number | null) => {
        setSalesOrder({
            ...salesOrder,
            client_id: clientId || undefined,
            contact_id: undefined,
            contact_name: '',
            contact_email: ''
        });
    };

    // Cuando se selecciona un contacto, copiar los datos
    const handleChangeContact = (contactId: number | null) => {
        if (contactId) {
            const selectedContact = clientContacts.find(c => c.id === contactId);
            if (selectedContact) {
                setSalesOrder({
                    ...salesOrder,
                    contact_id: contactId,
                    contact_name: selectedContact.name || '',
                    contact_email: selectedContact.email || ''
                });
            }
        } else {
            setSalesOrder({
                ...salesOrder,
                contact_id: undefined,
                contact_name: '',
                contact_email: ''
            });
        }
    };

    if (!salesOrder) return null;

    return (
        <>
            {/* Sector */}
            <div className="mb-2 row">
                <div className="col-md-12">
                    <label className="col-form-label">Sector</label>
                    <SelectTwo
                        name="sector_id"
                        id="sector_id"
                        inputValue={salesOrder.sector_id}
                        options={SelectTwoMapperOptions(sectors, 'id', ['name'])}
                        hasError={fieldHasError('sector_id') !== ''}
                        onChange={(value: any) => handleChangeSelectTwo('sector_id', value?.value)}
                        placeholder="Seleccione un sector"
                        onFocus={() => onFocusRemove('sector_id')}
                        isClearable={true}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('sector_id')}
                    </div>
                </div>
            </div>

            {/* Sociedad */}
            <div className="mb-2 row">
                <div className="col-md-12">
                    <label className="col-form-label">Sociedad *</label>
                    <SelectTwo
                        name="society_id"
                        id="society_id"
                        inputValue={salesOrder.society_id}
                        options={SelectTwoMapperOptions(societies, 'id', ['rut', 'business_name'])}
                        hasError={fieldHasError('society_id') !== ''}
                        onChange={(value: any) => handleChangeSelectTwo('society_id', value?.value)}
                        placeholder="Seleccione una sociedad"
                        onFocus={() => onFocusRemove('society_id')}
                        isClearable={true}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('society_id')}
                    </div>
                </div>
            </div>

            {/* Cliente */}
            <div className="mb-2 row">
                <div className="col-md-12">
                    <label className="col-form-label">Cliente *</label>
                    <SelectTwo
                        name="client_id"
                        id="client_id"
                        inputValue={salesOrder.client_id}
                        options={SelectTwoMapperOptions(
                            societyClients.filter(sc => sc.client && sc.client.id).map(sc => ({
                                id: sc.client?.id,
                                rut: sc.client?.rut,
                                business_name: sc.client?.business_name
                            })),
                            'id',
                            ['rut', 'business_name']
                        )}
                        hasError={fieldHasError('client_id') !== ''}
                        onChange={(value: any) => handleChangeClient(value?.value)}
                        placeholder="Seleccione un cliente"
                        onFocus={() => onFocusRemove('client_id')}
                        isClearable={true}
                        disabled={!salesOrder.society_id}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('client_id')}
                    </div>
                    {!salesOrder.society_id && (
                        <small className="text-muted">Primero debe seleccionar una sociedad</small>
                    )}
                    {salesOrder.society_id && societyClients.length === 0 && (
                        <small className="text-muted">No hay clientes disponibles para esta sociedad</small>
                    )}
                </div>
            </div>

            {/* Contacto */}
            {salesOrder.client_id && (
                <div className="mb-2 row">
                    <div className="col-md-12">
                        <label className="col-form-label">Contacto</label>
                        <SelectTwo
                            name="contact_id"
                            id="contact_id"
                            inputValue={salesOrder.contact_id}
                            options={SelectTwoMapperOptions(
                                clientContacts.filter(c => c && c.id),
                                'id',
                                ['name']
                            )}
                            hasError={false}
                            onChange={(value: any) => handleChangeContact(value?.value ?? null)}
                            placeholder="Seleccione un contacto"
                            isClearable={true}
                        />
                    </div>
                </div>
            )}

            {/* Nombre del Contacto | Email del Contacto */}
            <div className="mb-2 row">
                <div className="col-md-6">
                    <label className="col-form-label">Nombre del Contacto</label>
                    <input
                        type="text"
                        className={`form-control ${fieldHasError('contact_name')}`}
                        name="contact_name"
                        value={salesOrder.contact_name || ''}
                        onChange={(e) => setSalesOrder({ ...salesOrder, contact_name: e.target.value })}
                        placeholder="Nombre del contacto"
                        onFocus={() => onFocusRemove('contact_name')}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('contact_name')}
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="col-form-label">Email del Contacto</label>
                    <input
                        type="email"
                        className={`form-control ${fieldHasError('contact_email')}`}
                        name="contact_email"
                        value={salesOrder.contact_email || ''}
                        onChange={(e) => setSalesOrder({ ...salesOrder, contact_email: e.target.value })}
                        placeholder="Email del contacto"
                        onFocus={() => onFocusRemove('contact_email')}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('contact_email')}
                    </div>
                </div>
            </div>

            {/* Forma de Pago | Moneda */}
            <div className="mb-2 row">
                <div className="col-md-6">
                    <label className="col-form-label">Forma de Pago</label>
                    <SelectTwo
                        name="payment_method_id"
                        id="payment_method_id"
                        inputValue={salesOrder.payment_method_id}
                        options={SelectTwoMapperOptions(paymentMethods, 'id', ['name'])}
                        hasError={fieldHasError('payment_method_id') !== ''}
                        onChange={(value: any) => handleChangeSelectTwo('payment_method_id', value?.value)}
                        placeholder="Seleccione una forma de pago"
                        onFocus={() => onFocusRemove('payment_method_id')}
                        isClearable={true}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('payment_method_id')}
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="col-form-label">Moneda</label>
                    <SelectTwo
                        name="currency_id"
                        id="currency_id"
                        inputValue={salesOrder.currency_id}
                        options={SelectTwoMapperOptions(currencies, 'id', ['iso_code'])}
                        hasError={fieldHasError('currency_id') !== ''}
                        onChange={(value: any) => handleChangeSelectTwo('currency_id', value?.value)}
                        placeholder="Seleccione una moneda"
                        onFocus={() => onFocusRemove('currency_id')}
                        isClearable={true}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('currency_id')}
                    </div>
                </div>
            </div>

            {/* ¿Uno o dos pagos? */}
            <div className="mb-2 row">
                <div className="col-md-12">
                    <label className="col-form-label">¿Uno o dos pagos?</label>
                    <select
                        className={`form-control ${fieldHasError('number_of_payments')}`}
                        name="number_of_payments"
                        value={salesOrder.number_of_payments ?? 1}
                        onChange={(e) => {
                            const val = parseInt(e.target.value);
                            setSalesOrder({
                                ...salesOrder,
                                number_of_payments: val,
                                ...(val === 1 ? {
                                    payment_1_percentage: undefined,
                                    payment_2_percentage: undefined,
                                    payment_1_condition_id: undefined,
                                    payment_2_condition_id: undefined,
                                    payment_1_days: undefined,
                                    payment_2_days: undefined
                                } : {
                                    payment_1_percentage: 50,
                                    payment_2_percentage: 50
                                })
                            });
                        }}
                        onFocus={() => onFocusRemove('number_of_payments')}
                    >
                        <option value={1}>Un pago</option>
                        <option value={2}>Dos pagos</option>
                    </select>
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('number_of_payments')}
                    </div>
                </div>
            </div>

            {(salesOrder.number_of_payments ?? 1) === 1 ? (
                /* Un solo pago: Condición de Pago | Días */
                <div className="mb-2 row">
                    <div className="col-md-6">
                        <label className="col-form-label">Condición de Pago</label>
                        <SelectTwo
                            name="payment_condition_id"
                            id="payment_condition_id"
                            inputValue={salesOrder.payment_condition_id}
                            options={SelectTwoMapperOptions(paymentConditions, 'id', ['name'])}
                            hasError={fieldHasError('payment_condition_id') !== ''}
                            onChange={(value: any) => handleChangeSelectTwo('payment_condition_id', value?.value)}
                            placeholder="Seleccione una condición de pago"
                            onFocus={() => onFocusRemove('payment_condition_id')}
                            isClearable={true}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('payment_condition_id')}
                        </div>
                    </div>
                    <div className="col-md-6">
                        <label className="col-form-label">Días</label>
                        <input
                            type="number"
                            className={`form-control ${fieldHasError('days')}`}
                            name="days"
                            value={salesOrder.days !== undefined && salesOrder.days !== null ? salesOrder.days : ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                    setSalesOrder({ ...salesOrder, days: undefined });
                                } else {
                                    const numValue = parseInt(value);
                                    if (!isNaN(numValue) && numValue >= 0) {
                                        setSalesOrder({ ...salesOrder, days: numValue });
                                    }
                                }
                            }}
                            placeholder="Ingrese los días"
                            onFocus={() => onFocusRemove('days')}
                            min="0"
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('days')}
                        </div>
                    </div>
                </div>
            ) : (
                /* Dos pagos */
                <>
                    <div className="mb-2 row">
                        <div className="col-md-12">
                            <small className="text-muted">Los porcentajes deben sumar 100%</small>
                        </div>
                    </div>
                    <div className="mb-2 row">
                        <div className="col-md-6">
                            <label className="col-form-label">1er pago - Porcentaje (%)</label>
                            <input
                                type="number"
                                className={`form-control ${fieldHasError('payment_1_percentage')}`}
                                name="payment_1_percentage"
                                value={salesOrder.payment_1_percentage ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value ? parseInt(e.target.value) : undefined;
                                    setSalesOrder({ ...salesOrder, payment_1_percentage: v });
                                }}
                                placeholder="Ej: 50"
                                onFocus={() => onFocusRemove('payment_1_percentage')}
                                min="0"
                                max="100"
                            />
                            <div className="invalid-feedback" style={{ display: 'flex' }}>
                                {fieldErrorMessage('payment_1_percentage')}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <label className="col-form-label">2do pago - Porcentaje (%)</label>
                            <input
                                type="number"
                                className={`form-control ${fieldHasError('payment_2_percentage')}`}
                                name="payment_2_percentage"
                                value={salesOrder.payment_2_percentage ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value ? parseInt(e.target.value) : undefined;
                                    setSalesOrder({ ...salesOrder, payment_2_percentage: v });
                                }}
                                placeholder="Ej: 50"
                                onFocus={() => onFocusRemove('payment_2_percentage')}
                                min="0"
                                max="100"
                            />
                            <div className="invalid-feedback" style={{ display: 'flex' }}>
                                {fieldErrorMessage('payment_2_percentage')}
                            </div>
                        </div>
                    </div>
                    <div className="mb-2 row">
                        <div className="col-md-6">
                            <label className="col-form-label">1er pago - Condición de pago</label>
                            <SelectTwo
                                name="payment_1_condition_id"
                                inputValue={salesOrder.payment_1_condition_id}
                                options={SelectTwoMapperOptions(paymentConditions, 'id', ['name'])}
                                hasError={fieldHasError('payment_1_condition_id') !== ''}
                                onChange={(value: any) => handleChangeSelectTwo('payment_1_condition_id', value?.value)}
                                placeholder="Seleccione condición"
                                onFocus={() => onFocusRemove('payment_1_condition_id')}
                                isClearable={true}
                            />
                            <div className="invalid-feedback" style={{ display: 'flex' }}>
                                {fieldErrorMessage('payment_1_condition_id')}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <label className="col-form-label">1er pago - Días</label>
                            <input
                                type="number"
                                className={`form-control ${fieldHasError('payment_1_days')}`}
                                name="payment_1_days"
                                value={salesOrder.payment_1_days ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value ? parseInt(e.target.value) : undefined;
                                    setSalesOrder({ ...salesOrder, payment_1_days: v });
                                }}
                                placeholder="Días"
                                min="0"
                            />
                            <div className="invalid-feedback" style={{ display: 'flex' }}>
                                {fieldErrorMessage('payment_1_days')}
                            </div>
                        </div>
                    </div>
                    <div className="mb-2 row">
                        <div className="col-md-6">
                            <label className="col-form-label">2do pago - Condición de pago</label>
                            <SelectTwo
                                name="payment_2_condition_id"
                                inputValue={salesOrder.payment_2_condition_id}
                                options={SelectTwoMapperOptions(paymentConditions, 'id', ['name'])}
                                hasError={fieldHasError('payment_2_condition_id') !== ''}
                                onChange={(value: any) => handleChangeSelectTwo('payment_2_condition_id', value?.value)}
                                placeholder="Seleccione condición"
                                onFocus={() => onFocusRemove('payment_2_condition_id')}
                                isClearable={true}
                            />
                            <div className="invalid-feedback" style={{ display: 'flex' }}>
                                {fieldErrorMessage('payment_2_condition_id')}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <label className="col-form-label">2do pago - Días</label>
                            <input
                                type="number"
                                className={`form-control ${fieldHasError('payment_2_days')}`}
                                name="payment_2_days"
                                value={salesOrder.payment_2_days ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value ? parseInt(e.target.value) : undefined;
                                    setSalesOrder({ ...salesOrder, payment_2_days: v });
                                }}
                                placeholder="Días"
                                min="0"
                            />
                            <div className="invalid-feedback" style={{ display: 'flex' }}>
                                {fieldErrorMessage('payment_2_days')}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Condición de Entrega | Destino */}
            <div className="mb-2 row">
                <div className="col-md-6">
                    <label className="col-form-label">Condición de Entrega</label>
                    <SelectTwo
                        name="delivery_condition_id"
                        id="delivery_condition_id"
                        inputValue={salesOrder.delivery_condition_id}
                        options={SelectTwoMapperOptions(incoterms, 'id', ['name'])}
                        hasError={fieldHasError('delivery_condition_id') !== ''}
                        onChange={(value: any) => handleChangeSelectTwo('delivery_condition_id', value?.value)}
                        placeholder="Condición de entrega"
                        onFocus={() => onFocusRemove('delivery_condition_id')}
                        isClearable={true}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('delivery_condition_id')}
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="col-form-label">Lugar de Entrega</label>
                    <SelectTwo
                        name="destination_id"
                        id="destination_id"
                        inputValue={salesOrder.destination_id}
                        options={SelectTwoMapperOptions(locations, 'id', ['name'])}
                        hasError={fieldHasError('destination_id') !== ''}
                        onChange={(value: any) => handleChangeSelectTwo('destination_id', value?.value)}
                        placeholder="Lugar de Entrega"
                        onFocus={() => onFocusRemove('destination_id')}
                        isClearable={true}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('destination_id')}
                    </div>
                </div>
            </div>
        </>
    );
};

export default SalesOrderForm;
